import { SmsButton } from "../sms/Sms";
import React, { useState, useEffect } from "react";
import ShareTable from "../../../shares/tables/ShareTable";
import Search from "../../../shares/search/Search";
import { Modal, CircularProgress } from "@mui/material";
import AddGroups from "./modals/AddHomework";
import useFetch from "../../../../hooks/useFetch";
import { endpoints } from "../../../../api/endpoints";
import moment from "jalali-moment";
import { useSearchParams } from "react-router-dom";
import {
  DeleteOutline,
  EditOutlined,
  FileDownloadOutlined,
  AssignmentOutlined,
  AddRounded,
} from "@mui/icons-material";
import {
  PageHeader,
  StatusBadge,
  TableAction,
} from "../../../shares/dashboard/DashboardWidgets";

const HomeworkList = () => {
  const [newModal, setNewModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignments, setAssignments] = useState();
  const [id, setId] = useState();

  const handleClose = () => {
    setNewModal(false);
    setEdit(null);
  };

  const getAssignment = useFetch({
    method: "GET",
    safeRead: true,
    url: endpoints.management.assignments,
    params: { page: searchParams.get("page") ?? 1, search: searchParams.get("search") ?? "" },
    noHeader: false,
    trigger: true,
    setter: setAssignments,
  });

  const deleteAssignment = useFetch({
    method: "DELETE",
    url: `management/assignments/${id}/`,
    noHeader: false,
    trigger: false,
    caller: getAssignment,
    argFunc: () => {
      if (assignments?.results.length === 1) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", Math.max(1, Number(searchParams.get("page") || 1) - 1));
          return next;
        });
      }
      setId(null);
    },
  });

  const handleEdit = (id) => {
    setNewModal(true);
    setEdit(id);
  };

  const columns = [
    { id: "sms", label: "پیامک", render: (row) => <SmsButton kind="assignment" id={row.assignment_id} /> },
    { id: "AssignmentName", label: "نام" },
    { id: "assignment_headline", label: "مبحث" },
    {
      id: "assignment_available_time_start",
      label: "شروع",
      render: (row) =>
        moment(row.assignment_available_time_start, "YYYY/MM/DD")
          .locale("fa")
          .format("YYYY/MM/DD"),
    },
    {
      id: "assignment_available_time_end",
      label: "پایان",
      render: (row) =>
        moment(row.assignment_available_time_end, "YYYY/MM/DD")
          .locale("fa")
          .format("YYYY/MM/DD"),
    },
    {
      id: "assignment_finished",
      label: "وضعیت",
      render: (row) => (
        <StatusBadge
          label={row.assignment_finished ? "پایان یافته" : "در حال برگزاری"}
          tone={row.assignment_finished ? "neutral" : "success"}
        />
      ),
    },
    { id: "assignment_extra_score", label: "نمره اضافه" },
    {
      id: "assignment_group",
      label: "گروه ",
      render: (row) => (
        <span className="rounded-pill bg-surface-sunken px-md py-2xs text-xs text-ink-muted">
          {row.assignment_group.name}
        </span>
      ),
    },
    {
      id: "assignment_file",
      label: "فایل تکلیف",
      render: (row) =>
        row.assignment_file ? (
          <a
            href={row.assignment_file}
            download
            aria-label="دانلود فایل تکلیف"
            className="inline-flex h-[3.2rem] w-[3.2rem] items-center justify-center rounded-soft text-ink-muted transition-colors duration-150 hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <FileDownloadOutlined sx={{ fontSize: "1.9rem" }} />
          </a>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
    {
      id: "assignment_answer_file",
      label: "فایل جواب",
      render: (row) =>
        row.assignment_answer_file ? (
          <a
            href={row.assignment_answer_file}
            download
            aria-label="دانلود فایل جواب"
            className="inline-flex h-[3.2rem] w-[3.2rem] items-center justify-center rounded-soft text-ink-muted transition-colors duration-150 hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <FileDownloadOutlined sx={{ fontSize: "1.9rem" }} />
          </a>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
    {
      id: "edit",
      label: "ویرایش",
      render: (row) => (
        <TableAction
          icon={EditOutlined}
          onClick={() => handleEdit(row.assignment_id)}
          label="ویرایش تکلیف"
        />
      ),
    },
    {
      id: "delete",
      label: "حذف",
      render: (row) =>
        deleteAssignment.loading && id === row.assignment_id ? (
          <CircularProgress size="2rem" color="inherit" className="text-danger" />
        ) : (
          <TableAction
            icon={DeleteOutline}
            onClick={() => setId(row.assignment_id)}
            label="حذف تکلیف"
            tone="danger"
          />
        ),
    },
  ];

  useEffect(() => {
    id && deleteAssignment.reFetch();
  }, [id]);

  return (
    <div className="flex flex-col gap-xl pb-4xl">
      <PageHeader
        title="تکلیف‌ها"
        description="مدیریت تکالیف، فایل‌های صورت و پاسخ، و تخصیص به گروه‌ها"
        icon={AssignmentOutlined}
        actionLabel="افزودن تکلیف"
        actionIcon={AddRounded}
        onAction={() => setNewModal(true)}
      />

      <div className="flex flex-col gap-lg">
        <Search getFiltered={getAssignment} />

        <ShareTable
          data={assignments?.results ?? []}
          columns={columns}
          getPage={getAssignment}
          totalCount={assignments?.count}
          page={Number(searchParams.get("page")) || 1}
          setPage={setSearchParams}
        />
      </div>

      <Modal
        open={newModal}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AddGroups close={handleClose} get={getAssignment} edit={edit} />
      </Modal>
    </div>
  );
};

export default HomeworkList;
